import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { CollapsiblePanel } from './CollapsiblePanel';
import { ColorPicker } from '../controls/ColorPicker';
import { ShapePicker } from '../controls/ShapePicker';
import { ShapeWithColorOverride } from '../controls/ShapeWithColorOverride';
import { SegmentedToggle } from '../controls/SegmentedToggle';
import { GradientPicker } from '../controls/GradientPicker';
import './DesignPanel.css';

export const DesignPanel: React.FC = () => {
  const { t } = useTranslation('create');
  const config = useAppStore(state => state.config);
  const setConfig = useAppStore(state => state.setConfig);

  const fgTypeOptions: { value: 'solid' | 'linear-gradient' | 'radial-gradient', label: string }[] = [
    { value: 'solid', label: t('create.design.fgSolid', 'Solid') },
    { value: 'linear-gradient', label: t('create.design.fgLinear', 'Linear') },
    { value: 'radial-gradient', label: t('create.design.fgRadial', 'Radial') },
  ];

  const errorCorrectionOptions: { value: 'L' | 'M' | 'Q' | 'H', label: string }[] = [
    { value: 'L', label: 'L (7%)' },
    { value: 'M', label: 'M (15%)' },
    { value: 'Q', label: 'Q (25%)' },
    { value: 'H', label: 'H (30%)' },
  ];

  const handleCornerColorToggle = (color: string | null) => {
    setConfig({ cornerColor: color });
  };

  const handleEyeColorToggle = (color: string | null) => {
    setConfig({ eyeInnerColor: color });
  };

  return (
    <CollapsiblePanel title={t('create.design.title', 'Design Elements')} defaultExpanded={false}>
      <div className="design-section">
        <div className="design-section-title">{t('create.design.foreground', 'Foreground')}</div>
        <SegmentedToggle 
          options={fgTypeOptions} 
          value={config.foregroundType} 
          onChange={(val) => { setConfig({ foregroundType: val }); }} 
        />
        
        {config.foregroundType === 'solid' ? (
          <ColorPicker color={config.foregroundColor} onChange={c => { setConfig({ foregroundColor: c }); }} />
        ) : (
          <div className="gradient-controls">
            <ColorPicker label={t('create.design.color1', 'Color 1')} color={config.gradientColor1} onChange={c => { setConfig({ gradientColor1: c }); }} />
            <ColorPicker label={t('create.design.color2', 'Color 2')} color={config.gradientColor2} onChange={c => { setConfig({ gradientColor2: c }); }} />
            {config.foregroundType === 'linear-gradient' && (
              <GradientPicker label={t('create.design.direction', 'Direction')} direction={config.gradientDirection} onChange={d => { setConfig({ gradientDirection: d }); }} />
            )}
          </div>
        )}
      </div>

      <div className="design-section">
        <div className="design-section-title">{t('create.design.background', 'Background')}</div>
        <div className="bg-controls">
          <ColorPicker color={config.backgroundColor} onChange={c => { setConfig({ backgroundColor: c }); }} />
          <div className="checkbox-group">
            <input 
              type="checkbox" 
              id="bg-transparent" 
              checked={config.backgroundTransparent} 
              onChange={e => { setConfig({ backgroundTransparent: e.target.checked }); }} 
            />
            <label htmlFor="bg-transparent">{t('create.design.transparent', 'Transparent background')}</label>
          </div>
        </div>
      </div>

      <div className="design-section">
        <ShapePicker 
          label={t('create.design.moduleShape', 'Module Shape')}
          tooltip={t('create.design.moduleShapeTooltip', 'The individual data squares or dots that make up the main body of the QR code')}
          type="module"
          options={['square', 'rounded', 'dots', 'classy', 'classy-rounded', 'extra-rounded']}
          value={config.moduleShape}
          onChange={v => { setConfig({ moduleShape: v as 'square' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded' }); }}
        />
      </div>

      <ShapeWithColorOverride
        label={t('create.design.cornerShape', 'Corner Finder Shape')}
        tooltip={t('create.design.cornerShapeTooltip', 'The large alignment squares located in three corners of the QR code')}
        type="corner"
        options={['square', 'extra-rounded', 'dot']}
        shapeValue={config.cornerShape}
        onShapeChange={v => { setConfig({ cornerShape: v as 'square' | 'extra-rounded' | 'dot' }); }}
        colorValue={config.cornerColor}
        onColorChange={handleCornerColorToggle}
        overrideLabel={t('create.design.overrideColor', 'Override foreground color')}
        defaultOverrideColor={config.foregroundColor}
      />

      <ShapeWithColorOverride
        label={t('create.design.eyeShape', 'Eye Inner Shape')}
        tooltip={t('create.design.eyeShapeTooltip', 'The smaller solid square or dot inside the large corner finder shapes')}
        type="eye"
        options={['square', 'dot']}
        shapeValue={config.eyeInnerShape}
        onShapeChange={v => { setConfig({ eyeInnerShape: v as 'square' | 'dot' }); }}
        colorValue={config.eyeInnerColor}
        onColorChange={handleEyeColorToggle}
        overrideLabel={t('create.design.overrideColor', 'Override foreground color')}
        defaultOverrideColor={config.foregroundColor}
      />

      <div className="design-section">
        <SegmentedToggle 
          label={t('create.design.errorCorrection', 'Error Correction Level')}
          options={errorCorrectionOptions}
          value={config.errorCorrection}
          onChange={v => { setConfig({ errorCorrection: v }); }}
        />
        <div className="helper-text text-xs text-tertiary mt-xs">
          {t('create.design.errorCorrectionHelp', 'Higher levels increase robustness (better for logos/damage) but make the QR code denser.')}
        </div>
      </div>

    </CollapsiblePanel>
  );
};
